import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { DashboardCard } from "@/components/ui/dashboard-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigation } from '@/contexts/navigation-context';
import { useTimer } from "@/contexts/timer-context";
import { useProjectMeta } from "@/hooks/meta/useProjectMeta";
import { useStoryMeta } from "@/hooks/meta/useStoryMeta";
import { useDialog } from "@/hooks/useDialog";
import { useRefreshProjects, useUpdateProject } from "@/hooks/useProjects";
import { Story, useCreateStory, useSetCurrentStory, useStoriesByProject, useUpdateStory } from "@/hooks/useStories";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { cn } from "@udecode/cn";
import {
  BookOpen,
  CalendarIcon,
  ChevronLeft,
  Clock,
  Info,
  Pencil,
  PlusCircle,
  Square,
  X
} from "lucide-react";
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from "sonner";
import { IntroCard } from './components/intro-card';
import { StoriesDialog } from './stories-dialog';

import { UnifiedListLayout } from "@/components/layout/unified-list-layout";

interface StoryOverview {
  id: number;
  name: string;
  plotline?: string | null;
  status?: string | null;
  created_at: string;
  updated_at: string;
  word_count?: number;
  completion_percentage?: number;
  genre?: string | null;
}


function OverviewPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const updateStoryMutation = useUpdateStory();
  const createStoryMutation = useCreateStory();
  const setCurrentStoryMutation = useSetCurrentStory();
  const updateProjectMutation = useUpdateProject();
  const refreshProjects = useRefreshProjects();
  const { refetch: refetchNavigation, clearCache: clearNavigationCache } = useNavigation();
  const { data: stories = [] } = useStoriesByProject(projectId);



  // Timer Context
  const {
    isTimerRunning,
    sessionSeconds,
    startSession,
    pauseSession,
    resumeSession,
    endSession,
    formatTime
  } = useTimer();

  const [isStoriesDialogOpen, setIsStoriesDialogOpen] = useState(false);
  // Duplicate definition removed
  // const { refetch: refetchNavigation, clearCache: clearNavigationCache } = useNavigation();

  // Use optimization hooks
  const {
    name: projectTitle,
    description: projectDescription,
    genre: projectGenre,
    createdAt: projectCreatedAt,
    currentStoryId,
    isLoading: isProjectLoading,
    error: projectError
  } = useProjectMeta(projectId || null);

  const {
    name: currentStoryName,
    plotline: currentStoryDescription,
    genre: currentStoryGenre,
    status: currentStoryStatus,
    chapterCount,
    wordCount,
    lastUpdated: currentStoryUpdated,
    isLoading: isStoryLoading
  } = useStoryMeta(currentStoryId || null);

  // Construct current story object for UI compatibility
  const currentStory: StoryOverview | null = currentStoryId ? {
    id: parseInt(currentStoryId),
    name: currentStoryName || 'Untitled',
    plotline: currentStoryDescription,
    status: currentStoryStatus,
    created_at: '', // Not critical for overview display
    updated_at: currentStoryUpdated || '',
    word_count: wordCount,
    completion_percentage: 0, // Todo: Add to meta if needed
    genre: currentStoryGenre
  } : null;

  const isLoading = isProjectLoading;
  const error = projectError ? projectError.message : null;

  // Refresh meta is handled by query invalidation usually, but we can expose refetch from hook if needed
  // Refresh meta is handled by query invalidation usually

  const handleEditProject = () => {
    if (!projectId) return;

    openDialog("form", {
      title: "Edit Project Details",
      description: "Update your project information",
      fields: [
        { name: "name", label: "Project Name", type: "text", required: true, defaultValue: projectTitle },
        { name: "description", label: "Description", type: "textarea", defaultValue: projectDescription },
        { name: "genre", label: "Genre", type: "text", defaultValue: projectGenre }
      ],
      submitText: "Update Project"
    }, async (formData: { name: string; description?: string; genre?: string }) => {
      try {
        await updateProjectMutation.mutateAsync({
          id: projectId!,
          name: formData.name,
          description: formData.description,
          genre: formData.genre
        });

        // setProjectTitle(formData.name);
        // setProjectDescription(formData.description || 'No description provided.');
        // setProjectGenre(formData.genre || '');

        // refreshProjectMeta();
        refreshProjects();
        refreshProjects();
      } catch (err) {
        toast.error("Error", { description: "Failed to update project details" });
      }
    });
  };

  const handleStorySwitch = async (story: Story) => {
    const currentId = currentStory?.id ? String(currentStory.id) : undefined;
    const targetId = String(story.id);

    if (!projectId || targetId === currentId) return;

    try {
      if (setCurrentStoryMutation) {
        await setCurrentStoryMutation.mutateAsync({
          project_id: projectId,
          story_id: story.id
        });
      }

      clearNavigationCache(projectId);
      await refetchNavigation();
      // fetchInitialData(); // Removed, query auto-invalidates or we used cached data
      // Ideally we invalidate project meta to get new current_story_id



      toast.success("Story Switched", { description: `Now working on "${story.name}"` });
    } catch (error) {
      toast.error("Error", { description: "Failed to switch story" });
    }
  };

  const handleAddStory = () => {
    if (!projectId) return;

    openDialog("form", {
      title: "Create New Story",
      description: "Add a new story to your project",
      fields: [
        { name: "title", label: "Title", type: "text", required: true },
        { name: "description", label: "Description", type: "textarea" },
        { name: "genre", label: "Genre", type: "text" },
        {
          name: "status",
          label: "Status",
          type: "select",
          options: [
            { value: "draft", label: "Draft" },
            { value: "in_progress", label: "In Progress" },
            { value: "completed", label: "Completed" }
          ],
          defaultValue: "draft"
        }
      ],
      submitText: "Create Story"
    }, async (formData: { title: string; description?: string; genre?: string; status?: string }) => {
      try {
        const newStory = await createStoryMutation.mutateAsync({
          project_id: projectId,
          name: formData.title,
          plotline: formData.description,
          genre: formData.genre,
          status: formData.status as 'draft' | 'in_progress' | 'completed'
        });

        toast.success("Success", { description: "Story created successfully" });
        const storyId = typeof newStory.id === 'string' ? parseInt(newStory.id) : newStory.id;

        await setCurrentStoryMutation.mutateAsync({
          project_id: projectId,
          story_id: storyId.toString()
        });

        if (refreshProjects) refreshProjects();

      } catch (error: any) {
        toast.error("Error", { description: error.message || "Failed to create story" });
      }
    });
  };

  const handleEditStory = () => {
    if (!currentStory) return;

    openDialog("form", {
      title: "Edit Story",
      description: "Update story details",
      fields: [
        { name: "title", label: "Title", type: "text", required: true, defaultValue: currentStory.name },
        { name: "description", label: "Description", type: "textarea", defaultValue: currentStory.plotline },
        { name: "genre", label: "Genre", type: "text", defaultValue: currentStory.genre },
        {
          name: "status",
          label: "Status",
          type: "select",
          defaultValue: currentStory.status || 'draft',
          options: [
            { value: "draft", label: "Draft" },
            { value: "in_progress", label: "In Progress" },
            { value: "completed", label: "Completed" }
          ]
        }
      ],
      submitText: "Update Story"
    }, async (data: { title: string; description?: string; genre?: string; status?: string }) => {
      try {
        await updateStoryMutation.mutateAsync({
          id: currentStory.id.toString(),
          name: data.title,
          plotline: data.description,
          genre: data.genre,
          status: data.status as 'draft' | 'in_progress' | 'completed' | undefined
        });
        toast.success("Success", { description: "Story updated successfully" });
        // fetchInitialData();
      } catch (error) {
        toast.error("Error", { description: "Failed to update story" });
      }
    });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
    } catch (e) { return 'N/A'; }
  };



  // Wrapper for starting session
  const handleStartSession = () => {
    if (projectId) {
      startSession(projectId);
    }
  };

  const handleEndSession = async () => {
    if (projectId) {
      await endSession(projectId);
      await endSession(projectId);
      // fetchInitialData(); // Refresh stats
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-[220px] w-full bg-muted/20" />)}
        </div>
        <Skeleton className="h-[300px] w-full mt-8 bg-muted/20" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-destructive/20 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <X className="h-5 w-5" /> Error Loading Project
            </CardTitle>
            <CardDescription className="text-destructive/80">{error}</CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => window.location.reload()}>Try Again</Button>
            <Button variant="ghost" onClick={() => navigate('/workspace')}>Return to Workspace</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <UnifiedListLayout>
      {/* Project Header - Premium Design */}
      <div className="flex flex-col gap-4 mb-10">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{projectTitle}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {projectGenre && (
                <Badge variant="outline" className="rounded-md px-2.5 py-0.5 font-normal text-muted-foreground border-border bg-muted/5">
                  {projectGenre}
                </Badge>
              )}

              {projectGenre && (projectCreatedAt || projectDescription) && (
                <div className="h-4 w-[1px] bg-border/60" />
              )}

              {projectCreatedAt && (
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-3.5 w-3.5 opacity-70" />
                  <span className="text-xs">Started {formatDate(projectCreatedAt)}</span>
                </div>
              )}

              {projectCreatedAt && projectDescription && (
                <div className="h-4 w-[1px] bg-border/60" />
              )}

              {projectDescription && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-auto p-0 text-muted-foreground hover:text-foreground font-normal hover:bg-transparent flex items-center gap-1.5 rounded-none">
                      <Info className="h-3.5 w-3.5" />
                      <span className="text-xs">View Plotline</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96 p-6 border border-border shadow-xl rounded-lg bg-background" align="start">
                    <div className="space-y-3">
                      <h4 className="font-semibold text-base tracking-tight">Project Plotline</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {projectDescription}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          <Button variant="outline" size="sm" onClick={handleEditProject} className="hidden sm:flex h-9 bg-background/50 backdrop-blur-sm border-border/60 text-muted-foreground hover:text-foreground rounded-full">
            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
          </Button>
        </div>
      </div>

      {/* Introduction & Lifecycle Guide */}
      <IntroCard />

      {/* Genkit-Style 3-Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">

        {/* Card 1: Stories */}
        <DashboardCard
          title="Stories"
          count={stories.length}
          className="min-h-[340px]"
          delay={0.1}
          action={
            <Button variant="ghost" size="sm" className="h-6 text-xs text-primary hover:text-primary/80 px-0 hover:bg-transparent" onClick={() => setIsStoriesDialogOpen(true)}>
              View all <ChevronLeft className="h-3 w-3 rotate-180 ml-1" />
            </Button>
          }
        >
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
              {stories.map((story) => {
                const isCurrent = currentStory?.id && String(currentStory.id) === String(story.id);
                return (
                  <div
                    key={story.id}
                    className={cn(
                      "flex items-center justify-between p-3 px-4 border-b border-border hover:bg-muted/5 transition-colors cursor-pointer group",
                      isCurrent && "bg-primary/5 hover:bg-primary/10"
                    )}
                    onClick={() => handleStorySwitch(story)}
                  >
                    <div className="flex items-center gap-3">
                      <BookOpen className={cn("h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors", isCurrent && "text-primary")} />
                      <div className="flex flex-col">
                        <span className={cn("text-sm font-medium text-foreground/80 group-hover:text-foreground", isCurrent && "text-primary")}>{story.name}</span>
                        {story.status && <span className="text-[10px] text-muted-foreground capitalize">{story.status}</span>}
                      </div>
                    </div>
                    {isCurrent && <Badge variant="outline" className="text-[10px] h-5 border-primary/20 text-primary bg-primary/5">Active</Badge>}
                  </div>
                );
              })}
            </div>

            <div
              className="p-3 px-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary cursor-pointer border-t border-border bg-muted/5 hover:bg-muted/10 transition-colors mt-auto"
              onClick={handleAddStory}
            >
              <PlusCircle className="h-4 w-4" />
              <span>Create new story</span>
            </div>
          </div>
        </DashboardCard>

        {/* Card 2: Writing Session (Restored) */}
        <DashboardCard title="Writing Session" className="min-h-[220px]" delay={0.2}>
          <div className="flex flex-col items-center justify-center h-full p-6 relative">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-muted/5 pointer-events-none" />

            <div className="text-center mb-6 relative z-10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className={cn("h-2 w-2 rounded-full", isTimerRunning ? "bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]" : "bg-muted-foreground/30")} />
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {isTimerRunning ? "Recording" : "Ready"}
                </span>
              </div>
              <div className="text-5xl font-mono font-bold tracking-tighter text-foreground tabular-nums">
                {formatTime(sessionSeconds)}
              </div>
            </div>

            <div className="flex w-full gap-3 relative z-10">
              {!isTimerRunning ? (
                <Button variant="outline" onClick={sessionSeconds > 0 ? resumeSession : handleStartSession} className="flex-1 border-primary/20 text-primary hover:text-primary hover:bg-primary/5 hover:border-primary/50 font-medium transition-all">
                  <Clock className="h-4 w-4 mr-2" />
                  {sessionSeconds > 0 ? "Resume" : "Start Session"}
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={pauseSession} className="flex-1 border-border bg-background/50 backdrop-blur-sm">
                    Pause
                  </Button>
                  <Button variant="ghost" onClick={handleEndSession} className="px-3 text-destructive hover:text-destructive hover:bg-destructive/10">
                    <Square className="h-4 w-4 fill-current" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </DashboardCard>

        {/* Card 3: Overview / Stats */}
        <DashboardCard title="Overview" className="min-h-[220px]" delay={0.3}>
          <div className="flex flex-col h-full divide-y divide-border">
            <div className="flex-1 p-4 flex flex-col justify-center gap-1 group">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Words</span>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-mono font-medium">{currentStory?.word_count?.toLocaleString() || 0}</span>
              </div>
            </div>
            <div className="flex-1 p-4 flex flex-col justify-center gap-1 group">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Chapters</span>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-mono font-medium">{chapterCount}</span>
              </div>
            </div>
            <div className="flex-1 p-4 flex flex-col justify-center gap-1 group">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Last Updated</span>
              <div className="flex items-end justify-between">
                <span className="text-sm font-medium">{formatDate(currentStory?.updated_at)}</span>
              </div>
            </div>
          </div>
        </DashboardCard>

      </div>

      {/* Bottom Section: Active Story Details (Table view) */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active Story Details</h2>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-xs h-8 text-muted-foreground hover:text-foreground" onClick={handleEditStory}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Details
            </Button>
          </div>
        </div>

        <Card className="bg-card border-border shadow-sm backdrop-blur-sm overflow-hidden rounded-2xl">
          {currentStory ? (
            <div className="w-full text-left text-sm">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-border bg-muted/5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <div className="col-span-4 pl-2">Name</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-4">Plotline</div>
                <div className="col-span-2 text-right pr-2">Updated</div>
              </div>
              <div
                className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-muted/5 transition-colors cursor-pointer group"
                onClick={() => navigate(`/projects/${projectId}/stories/${currentStory.id}`)}
              >
                <div className="col-span-4 font-medium text-foreground flex items-center gap-3 pl-2">
                  <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors ring-1 ring-primary/20">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <span className="group-hover:text-primary transition-colors">{currentStory.name}</span>
                </div>
                <div className="col-span-2">
                  <Badge variant="outline" className={cn(
                    "capitalize border-border px-2",
                    currentStory.status === 'completed' ? "text-green-400 bg-green-400/5" : "text-muted-foreground bg-muted/10"
                  )}>
                    {currentStory.status?.replace('_', ' ') || 'Draft'}
                  </Badge>
                </div>
                <div className="col-span-4 text-muted-foreground line-clamp-1 text-xs">
                  {currentStory.plotline || "No description provided."}
                </div>
                <div className="col-span-2 text-right text-muted-foreground font-mono text-xs pr-2">
                  {formatDate(currentStory.updated_at)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[120px] text-muted-foreground py-8">
              <p className="text-sm mb-2">No active story selected.</p>
              <Button variant="outline" size="sm" onClick={handleAddStory} className="h-8 text-xs">
                <PlusCircle className="h-3 w-3 mr-2" />
                Create a story
              </Button>
            </div>
          )}
        </Card>
      </div>

      <StoriesDialog
        open={isStoriesDialogOpen}
        onOpenChange={setIsStoriesDialogOpen}
        stories={stories}
        currentStoryId={currentStory?.id}
        onSwitchStory={(story) => handleStorySwitch(story)}
        onAddStory={handleAddStory}
      />
    </UnifiedListLayout>
  );
}

export default OverviewPage;